import { useContext, useMemo } from 'react';
import { Context as CustomerContext } from '../contexts/useCustomer';
import { StorageKey } from './model/StorageKey';
import { useCookieStorage } from './useCookieStorage';
import type { Customer } from '../bff-api/customers/model';

interface CulinaryClassPreferredStoreResult {
    data: string | null;
    isLoading: boolean;
    isFetched: boolean;
}

const getCustomerStoreId = (customer: Customer | undefined): string | null => {
    if (customer && typeof customer === 'object' && 'c_storeID' in customer) {
        return customer.c_storeID ?? null;
    }
    return null;
};

/**
 * Hook to get the preferred culinary class store ID.
 * - For registered users: returns `customer.c_storeID` from the profile
 * - For guest users: returns the value from the `storeID` cookie
 */
export const useCulinaryClassPreferredStore = (): CulinaryClassPreferredStoreResult => {
    const { customer, isRegistered, isLoading: isCustomerLoading } = useContext(CustomerContext);
    const cookieStorage = useCookieStorage(StorageKey.CULINARY_CLASS_PREFERRED_STORE);

    return useMemo(() => {
        if (isRegistered) {
            return {
                data: getCustomerStoreId(customer as Customer),
                isLoading: isCustomerLoading,
                isFetched: !isCustomerLoading,
            };
        }

        return {
            data: cookieStorage.data ?? null,
            isLoading: cookieStorage.isLoading,
            isFetched: cookieStorage.isFetched,
        };
    }, [
        isRegistered,
        customer,
        isCustomerLoading,
        cookieStorage.data,
        cookieStorage.isLoading,
        cookieStorage.isFetched,
    ]);
};
